/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */
/* global clearInterval, console, setInterval, fetch, process, window, Promise, atob, OfficeRuntime, Excel, Intl, CustomFunctions, localStorage */
import * as Msal from 'msal';
import { StringDict } from 'msal/lib-commonjs/MsalTypes';
import { dialogFallback } from '../helpers/fallbackauthhelper';
import { QueryParams } from '../app/types/query-params';
import { getDateTime } from './dateHelper';
import {
  loadFundamentalData,
  loadFundamentalDataDetailed,
} from './fundamentalData';
import { getPrices, getVersionedPrices } from './historicPrices';
import { loadLimprReport } from './limpr';
import { loadLivePrices, loadLivePricesByIds } from './livePrices';
import * as loadProfileFunctions from './loadProfiles';
import * as meteringPointFunctions from './meteringPoints';
import { setNumberFormat } from './util';
import { DateTime } from 'luxon';

declare global {
  interface Window {
    officeInitialized: boolean;
    OAuthToken: any;
    expiresOn: Date;
    api_url: string;
    jsDateToExcelDate: any;
  }
}
window.api_url = 'https://api.inercomp.com/v1';
// const resolution_formats = ['1 min', '5 min', '15 min', '1 hour', '1 day', '1 week', '1 month', '1 quarter', '6 month', '1 year'];

const msalConfig: Msal.Configuration = {
  auth: {
    clientId: '4dfd3b9f-60db-4838-bb62-2505606179cb',
    authority:
      'https://login.microsoftonline.com/ebb24e74-d367-4ff2-8906-5559fc0c8078',
    redirectUri: process.env['APPSETTING_REDIRECT_URI'],
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
};

const userAgentApp: Msal.UserAgentApplication = new Msal.UserAgentApplication(
  msalConfig
);

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

async function fallBackAuth(request: {
  scopes?: string[] | undefined;
  loginHint: any;
  authority?: string | undefined;
  extraScopesToConsent?: string[] | undefined;
  prompt?: string | undefined;
  extraQueryParameters?: StringDict | undefined;
  claimsRequest?: string | undefined;
  state?: string | undefined;
  correlationId?: string | undefined;
  account?: Msal.Account | undefined;
  sid?: string | undefined;
  forceRefresh?: boolean | undefined;
  redirectUri?: string | undefined;
  redirectStartPage?: string | undefined;
  authorityMetadata?: string | undefined;
  onRedirectNavigate?: ((url: string) => boolean | void) | undefined;
}) {
  if (userAgentApp.getAccount()) {
    try {
      request.loginHint =
        userAgentApp.getAccount().idToken['preferred_username'];
      let response = await userAgentApp.acquireTokenSilent(request);
      window.OAuthToken = response.accessToken;
      window.expiresOn = response.expiresOn;
    } catch (e) {
      console.debug(e);
      dialogFallback();
    }
  } else {
    dialogFallback();
  }
}

export async function updateToken() {
  if (window.expiresOn === undefined || window.expiresOn < new Date()) {
    let request: any = {
      scopes: [`https://graph.microsoft.com/User.Read`],
      loginHint: null,
      authority: userAgentApp.authority,
    };
    if (OfficeRuntime !== undefined && OfficeRuntime !== null) {
      try {
        let token = await OfficeRuntime.auth.getAccessToken({
          allowConsentPrompt: true,
        });
        const parsedToken = parseJwt(token);
        request.loginHint = parsedToken.preferred_username;
        const response = await userAgentApp.acquireTokenSilent(request);
        window.OAuthToken = response.accessToken;
        window.expiresOn = response.expiresOn;
      } catch (e) {
        console.error(e);
        await fallBackAuth(request);
      }
    } else {
      await fallBackAuth(request);
    }
  }
}

/**
 * Gets the live prices from the backend
 * @customfunction GETLIVEPRICESBYIDS
 * @param {string} ids An array of EMSA ids, for which the time series shall be retrieved. (Splitted by ',')
 * @param {number} interval to restart (in seconds) [if it shall be set to standard 30minutes type -1]
 * @param {string} headers an array of headers that shall be included in the output
 * @param {string} unit
 * @param {string} currency
 * @param {CustomFunctions.StreamingInvocation<string[][]>} invocation
 */
export async function getLivePricesByIds(
  ids: string,
  interval: number,
  headers: string,
  unit: string,
  currency: string,
  invocation: CustomFunctions.StreamingInvocation<string[][]>
) {
  let result = await loadLivePricesByIds(ids, headers, unit, currency);
  await setNumberFormat(
    '0.00',
    result.length - 1,
    result[0].length - 3,
    0,
    2,
    invocation.address
  );
  await setNumberFormat(
    'dd.mm.yyyy hh:MM:ss',
    result.length - 1,
    1,
    0,
    headers.split(',').length + 1,
    invocation.address
  );
  const header_arr = headers.split(',');
  await setNumberFormat(
    '(@)',
    result.length - 1,
    1,
    0,
    2 + header_arr.indexOf('change_percent'),
    invocation.address,
    header_arr.indexOf('last') - header_arr.indexOf('change_percent')
  );
  invocation.setResult(result);
  let timer = setInterval(
    async () => {
      let result = await loadLivePricesByIds(ids, headers, unit, currency);
      invocation.setResult(result);
    },
    interval > 0 ? interval * 1000 : 60 * 1000
  );

  invocation.onCanceled = () => {
    clearInterval(timer);
  };
}

/**
 * @customfunction GETLIVEPRICESADVANCED
 * @param {string} market_area_id
 * @param {string[][]} price_types
 * @param {string[][]} exchanges
 * @param {string} product
 * @param {string} headers an array of headers that shall be included in the output
 * @param {string} unit
 * @param {string} currency
 * @param {number} interval to restart (in seconds) [if it shall be set to standard 30minutes type -1]
 * @param {CustomFunctions.StreamingInvocation<string[][]>} invocation
 */
export async function getLivePricesN(
  market_area_id: string,
  price_types: string[][],
  exchanges: string[][],
  product: string,
  headers: string,
  unit: string,
  currency: string,
  interval: number,
  invocation: CustomFunctions.StreamingInvocation<string[][]>
) {
  let context = new Excel.RequestContext();
  let address = context.workbook.getSelectedRange().getCell(0, 0);
  let lsJumpToNextCell = localStorage.getItem('feeder_jump_to_next_cell');
  if (lsJumpToNextCell !== null && JSON.parse(lsJumpToNextCell)) {
    let range = context.workbook
      .getSelectedRange()
      .getResizedRange(1, headers.split(',').length + 2)
      .getLastColumn()
      .getCell(0, 0);
    range.select();
  }
  address.load(['address']);
  await context.sync();
  let result = await loadLivePrices(
    market_area_id,
    ([] as any[]).concat.apply([], price_types),
    ([] as any[]).concat.apply([], exchanges),
    product,
    headers,
    unit,
    currency
  );
  await setNumberFormat(
    '0.00',
    result.length,
    result[0].length - 3,
    0,
    2,
    address.address.split('!')[1]
  );
  await setNumberFormat(
    'dd.mm.yyyy hh:MM:ss',
    result.length,
    0,
    0,
    headers.split(',').length + 1,
    address.address.split('!')[1]
  );
  let header_arr = headers.split(',');
  await setNumberFormat(
    '(@)',
    result.length,
    0,
    0,
    2 + header_arr.indexOf('change_percent'),
    address.address.split('!')[1],
    header_arr.indexOf('last') - header_arr.indexOf('change_percent')
  );
  invocation.setResult(result);
  let timer = setInterval(
    async () => {
      let result = await loadLivePrices(
        market_area_id,
        ([] as any[]).concat.apply([], price_types),
        ([] as any[]).concat.apply([], exchanges),
        product,
        headers,
        unit,
        currency
      );
      invocation.setResult(result);
    },
    interval > 0 ? interval * 1000 : 60 * 1000
  );

  invocation.onCanceled = () => {
    clearInterval(timer);
  };
}

/**
 * @customfunction GETLIVEPRICES
 * @param {string} market_area_id
 * @param {string} price_type
 * @param {string} exchange
 * @param {string} product
 * @param {string} headers an array of headers that shall be included in the output
 * @param {string} unit
 * @param {string} currency
 * @param {number} interval to restart (in seconds) [if it shall be set to standard 30minutes type -1]
 * @param {CustomFunctions.StreamingInvocation<string[][]>} invocation
 */
export async function getLivePrices(
  market_area_id: string,
  price_type: string,
  exchange: string,
  product: string,
  headers: string,
  unit: string,
  currency: string,
  interval: number,
  invocation: CustomFunctions.StreamingInvocation<string[][]>
) {
  let context = new Excel.RequestContext();
  let address = context.workbook.getSelectedRange().getCell(0, 0);
  let lsJumpToNextCell = localStorage.getItem('feeder_jump_to_next_cell');
  if (lsJumpToNextCell !== null && JSON.parse(lsJumpToNextCell)) {
    let range = context.workbook
      .getSelectedRange()
      .getResizedRange(1, headers.split(',').length + 2)
      .getLastColumn()
      .getCell(0, 0);
    range.select();
  }
  address.load(['address']);
  await context.sync();
  let result = await loadLivePrices(
    market_area_id,
    [price_type],
    [exchange],
    product,
    headers,
    unit,
    currency
  );
  await setNumberFormat(
    '0.00',
    result.length,
    result[0].length - 3,
    0,
    2,
    address.address.split('!')[1]
  );
  await setNumberFormat(
    'dd.mm.yyyy hh:MM:ss',
    result.length,
    0,
    0,
    headers.split(',').length + 1,
    address.address.split('!')[1]
  );
  let header_arr = headers.split(',');
  await setNumberFormat(
    '(@)',
    result.length,
    0,
    0,
    2 + header_arr.indexOf('change_percent'),
    address.address.split('!')[1],
    header_arr.indexOf('last') - header_arr.indexOf('change_percent')
  );
  invocation.setResult(result);
  let timer = setInterval(
    async () => {
      let result = await loadLivePrices(
        market_area_id,
        [price_type],
        [exchange],
        product,
        headers,
        unit,
        currency
      );
      invocation.setResult(result);
    },
    interval > 0 ? interval * 1000 : 60 * 1000
  );

  invocation.onCanceled = () => {
    clearInterval(timer);
  };
}

/**
 * Gets the historic prices from the backend
 * @customfunction HPFC GETFORWARDCURVE
 * @requiresAddress
 * @param {string[][]} product_codes An array of EMSA product_codes, for which the time series shall be retrieved. Input format: {"product_code";"product_code";".."}
 * @param {string} currency The ISO 4217 code of the desired currency.
 * @param {string} unit The desired unit of the returned data. ( kWh, MWh, GWh )
 * @param {number|string} start_date The date starting from which the data shall be retrieved. In the format YYYY-MM-DD
 * @param {number|string} end_date The date till which the data shall be retrieved. In the format YYYY-MM-DD.
 * @param {string} resolution (x min, x hour, x day, x week, x month, x year). If not set, prices will be returned in the same resolution as inserted.
 * @param {string} aggregation The level on which data points shall be aggregated to. (x min, x hour, x day, x week, x month, x year)
 * @param {string} aggregation_scope (base, peak, off_peak)
 * @param {string} aggregation_method (MIN, MAX, AVG)
 * @param {string} output_format
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {string} nv_default
 * @param {string} time_zone
 * @param {number} start_of_day
 * @param invocation
 * @returns {number[][]} A dynamic array of prices for given timespan
 */
export async function getHPFC(
  product_codes: string[][],
  currency?: string,
  unit?: string,
  resolution?: string,
  aggregation?: string,
  aggregation_scope?: string,
  aggregation_method?: string,
  start_date?: number | string,
  end_date?: number | string,
  output_format?: string,
  auto_update?: boolean,
  nv_default?: string,
  time_zone?: string,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  return await getPrices(
    product_codes,
    currency || '',
    unit || '',
    resolution || '',
    aggregation || '',
    aggregation_scope || '',
    aggregation_method || '',
    start_date || DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd'),
    end_date || DateTime.now().toFormat('yyyy-MM-dd'),
    false,
    output_format || '',
    auto_update ?? true,
    nv_default || '',
    time_zone || '',
    true,
    start_of_day ?? null,
    invocation!
  );
}

/**
 * Gets the historic prices from the backend
 * @customfunction GETHISTORICPRICES
 * @requiresAddress
 * @param {string[][]} product_codes An array of EMSA product_codes, for which the time series shall be retrieved. Input format: {"product_code";"product_code";".."}
 * @param {string} currency The ISO 4217 code of the desired currency.
 * @param {string} unit The desired unit of the returned data. ( kWh, MWh, GWh )
 * @param {number|string} start_date The date starting from which the data shall be retrieved. In the format YYYY-MM-DD
 * @param {number|string} end_date The date till which the data shall be retrieved. In the format YYYY-MM-DD.
 * @param {string} resolution (x min, x hour, x day, x week, x month, x year). If not set, prices will be returned in the same resolution as inserted.
 * @param {string} aggregation The level on which data points shall be aggregated to. (x min, x hour, x day, x week, x month, x year)
 * @param {string} aggregation_scope (base, peak, off_peak)
 * @param {string} aggregation_method (MIN, MAX, AVG)
 * @param {boolean} last_price_only Only show the latest price from the products
 * @param {string} output_format [default;diagram]
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {string} nv_default
 * @param {string} time_zone
 * @param {number} start_of_day
 * @param invocation
 * @returns {number[][]} A dynamic array of prices for given timespan
 */
export async function getHistoricPrices(
  product_codes: string[][],
  currency?: string,
  unit?: string,
  resolution?: string,
  aggregation?: string,
  aggregation_scope?: string,
  aggregation_method?: string,
  start_date?: number | string,
  end_date?: number | string,
  last_price_only?: boolean,
  output_format?: string,
  auto_update?: boolean,
  nv_default?: string,
  time_zone?: string,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  return await getPrices(
    product_codes,
    currency || '',
    unit || '',
    resolution || '',
    aggregation || '',
    aggregation_scope || '',
    aggregation_method || '',
    start_date || DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd'),
    end_date || DateTime.now().toFormat('yyyy-MM-dd'),
    last_price_only ?? false,
    output_format || '',
    auto_update ?? true,
    nv_default || '',
    time_zone || '',
    false,
    start_of_day ?? null,
    invocation!
  );
}

/**
 * Gets the historic prices from the backend
 * @customfunction GETVERSIONEDPRICES
 * @requiresAddress
 * @param {string[][]} product_codes An array of EMSA product_codes, for which the time series shall be retrieved. Input format: {"product_code";"product_code";".."}
 * @param {string} currency The ISO 4217 code of the desired currency.
 * @param {string} unit The desired unit of the returned data. ( kWh, MWh, GWh )
 * @param {number|string} start_date The date starting from which the data shall be retrieved. In the format YYYY-MM-DD
 * @param {number|string} end_date The date till which the data shall be retrieved. In the format YYYY-MM-DD.
 * @param {string} resolution (x min, x hour, x day, x week, x month, x year). If not set, prices will be returned in the same resolution as inserted.
 * @param {string} aggregation The level on which data points shall be aggregated to. (x min, x hour, x day, x week, x month, x year)
 * @param {string} aggregation_scope (base, peak, off_peak)
 * @param {string} aggregation_method (MIN, MAX, AVG)
 * @param output_format
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {string} nv_default
 * @param {string} time_zone
 * @param {number|string} max_version
 * @param {number} start_of_day
 * @param invocation
 * @returns {number[][]} A dynamic array of prices for given timespan
 */
export async function getHistoricPricesVersioned(
  product_codes: string[][],
  currency?: string,
  unit?: string,
  resolution?: string,
  aggregation?: string,
  aggregation_scope?: string,
  aggregation_method?: string,
  start_date?: number | string,
  end_date?: number | string,
  output_format?: string,
  auto_update?: boolean,
  nv_default?: string,
  time_zone?: string,
  max_version?: string | number,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  return await getVersionedPrices(
    product_codes,
    currency || '',
    unit || '',
    resolution || '',
    aggregation || '',
    aggregation_scope || '',
    aggregation_method || '',
    start_date || DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd'),
    end_date || DateTime.now().toFormat('yyyy-MM-dd'),
    false,
    output_format || '',
    auto_update ?? true,
    nv_default || '',
    time_zone || '',
    true,
    max_version || '',
    start_of_day ?? null,
    invocation!
  );
}

/**
 * Gets the fundamental data from the backend
 * @customfunction GETFUNDAMENTALDATAOLD
 * @param {number} market_area_id id of the market area
 * @return {string[][]}
 */
export async function getFundamentalDataOld(market_area_id?: number) {
  if (market_area_id === undefined) {
    return [['please give a market_area_id']];
  }
  return await loadFundamentalData(market_area_id);
}

/**
 * @customfunction GETFUNDAMENTALDATADETAILED
 * @requiresAddress
 * @param {string[][]} product_codes An array of EMSA product_codes, for which the time series shall be retrieved. Input format: {"product_code";"product_code";".."}
 * @param {string} unit The desired unit of the returned data. ( kWh, MWh, GWh )
 * @param {number|string} start_date The date starting from which the data shall be retrieved. In the format YYYY-MM-DD
 * @param {number|string} end_date The date till which the data shall be retrieved. In the format YYYY-MM-DD.
 * @param {string} resolution (x min, x hour, x day, x week, x month, x year). If not set, prices will be returned in the same resolution as inserted.
 * @param {string} aggregation The level on which data points shall be aggregated to. (x min, x hour, x day, x week, x month, x year)
 * @param {string} aggregation_scope (base, peak, off_peak)
 * @param {string} aggregation_method (MIN, MAX, AVG)
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {boolean} detailed
 * @param {string} nv_default
 * @param {string} time_zone
 * @param {number} start_of_day
 * @param invocation
 * @return {string[][]} A dynamic array containing fundamental data
 */
export async function getFundamentalDataDetailed(
  product_codes: string[][],
  unit?: string,
  start_date?: string | number,
  end_date?: string | number,
  resolution?: string,
  aggregation?: string,
  aggregation_scope?: string,
  aggregation_method?: string,
  auto_update?: boolean,
  detailed?: boolean,
  nv_default?: string,
  time_zone?: string,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  return await loadFundamentalDataDetailed(
    product_codes,
    start_date || DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd'),
    end_date || DateTime.now().toFormat('yyyy-MM-dd'),
    detailed ?? true,
    new QueryParams(
      resolution || '',
      aggregation || '',
      aggregation_scope || '',
      aggregation_method || '',
      nv_default || '',
      unit || '',
      '',
      time_zone || '',
      false,
      auto_update ?? true,
      start_of_day
    ),
    '',
    invocation!
  );
}

/**
 * @customfunction GETFUNDAMENTALDATA
 * @requiresAddress
 * @param {string[][]} product_codes An array of EMSA product_codes, for which the time series shall be retrieved. Input format: {"product_code";"product_code";".."}
 * @param {string} unit The desired unit of the returned data. ( kWh, MWh, GWh )
 * @param {number|string} start_date The date starting from which the data shall be retrieved. In the format YYYY-MM-DD
 * @param {number|string} end_date The date till which the data shall be retrieved. In the format YYYY-MM-DD.
 * @param {string} resolution (x min, x hour, x day, x week, x month, x year). If not set, prices will be returned in the same resolution as inserted.
 * @param {string} aggregation The level on which data points shall be aggregated to. (x min, x hour, x day, x week, x month, x year)
 * @param {string} aggregation_scope (base, peak, off_peak)
 * @param {string} aggregation_method (MIN, MAX, AVG)
 * @param {boolean} latest_value only show latest value
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {boolean} detailed
 * @param {string} nv_default
 * @param {string} time_zone
 * @param {string} [diagram]
 * @param {number} start_of_day
 * @param invocation
 * @return {string[][]} A dynamic array containing fundamental data
 */
export async function getFundamentalDataDiagram(
  product_codes: string[][],
  unit?: string,
  start_date?: string | number,
  end_date?: string | number,
  resolution?: string,
  aggregation?: string,
  aggregation_scope?: string,
  aggregation_method?: string,
  latest_value?: boolean,
  auto_update?: boolean,
  detailed?: boolean,
  nv_default?: string,
  time_zone?: string,
  diagram?: string,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  return await loadFundamentalDataDetailed(
    product_codes,
    start_date || DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd'),
    end_date || DateTime.now().toFormat('yyyy-MM-dd'),
    detailed ?? true,
    new QueryParams(
      resolution || '',
      aggregation || '',
      aggregation_scope || '',
      aggregation_method || '',
      nv_default || '',
      unit || '',
      '',
      time_zone || '',
      latest_value ?? false,
      auto_update ?? true,
      '',
      start_of_day
    ),
    diagram === undefined ||
      diagram === null ||
      diagram === '' ||
      diagram === 'default'
      ? ''
      : 'diagram',
    invocation!
  );
}

/**
 * @customfunction GETLOADPROFILEBYID
 * @requiresAddress
 * @param {string[][]} load_profile_ids
 * @param {string} unit (kW, MW, GW, kWh, MWh, GWh)
 * @param {string} resolution (15 min, 1 hour, 1 day, 1 month)
 * @param {number|string} start_date optional starting point of the time_series_data. (YYYY-MM-DDTHH:MM:SSZ)
 * @param {number|string} end_date optional ending point of the time_series_data. (YYYY-MM-DDTHH:MM:SSZ)
 * @param {boolean} enforce_length Extend the time_series from start_date to end_date if there is no data present in the db.
 * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
 * @param {number} start_of_day
 * @param invocation
 * @return {string[][]}
 */
export async function getLoadprofileById(
  load_profile_ids: string[][],
  unit?: string,
  resolution?: string,
  start_date?: number | string,
  end_date?: number | string,
  enforce_length?: boolean,
  auto_update?: boolean,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  let id_arr = loadProfileFunctions.parseIds(load_profile_ids);
  const start = start_date
    ? getDateTime(start_date)
    : DateTime.now().minus({ years: 1 });
  let end = end_date ? getDateTime(end_date) : DateTime.now();
  return loadProfileFunctions.getLoadProfilesByIds(
    id_arr,
    resolution || '',
    unit || '',
    start,
    end,
    enforce_length,
    auto_update ?? true,
    start_of_day ?? null,
    invocation!.address!,
    window.api_url
  );
}

/**
 * @customfunction GETAGGREGATEDLOADPROFILES
 * @requiresAddress
 * @param {string[][]} load_profile_ids
 * @param {string} contract_portfolio_id
 * @param {string[][]} managing_portfolio_ids
 * @param {string} load_profile_quality
 * @param {string} metering_point_type
 * @param {string} resolution
 * @param {string} unit
 * @param {number|string} start_date
 * @param {number|string} end_date
 * @param {boolean} gap_warning
 * @param {boolean} auto_update
 * @param {boolean} [exclude_gap_lps]
 * @param {string} [delivery_type]
 * @param {boolean} [consider_delivery_period]
 * @param {number} start_of_day
 * @param invocation
 * @return {string[][]}
 */
export async function getAggregatedLoadprofiles(
  load_profile_ids: string[][],
  contract_portfolio_id: string,
  managing_portfolio_ids?: string[][],
  load_profile_quality?: string,
  metering_point_type?: string,
  resolution?: string,
  unit?: string,
  start_date?: number | string,
  end_date?: number | string,
  gap_warning?: boolean,
  auto_update?: boolean,
  exclude_gap_lps?: boolean,
  delivery_type?: string,
  consider_delivery_period?: boolean,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation
) {
  const start = start_date
    ? getDateTime(start_date)
    : DateTime.now().minus({ years: 1 });
  const end = end_date ? getDateTime(end_date) : DateTime.now();
  return await loadProfileFunctions.getAggregatedLoadProfiles(
    load_profile_ids,
    contract_portfolio_id,
    managing_portfolio_ids || [],
    load_profile_quality || '',
    metering_point_type || '',
    resolution || '',
    unit || '',
    start,
    end,
    gap_warning ?? false,
    auto_update ?? true,
    exclude_gap_lps ?? false,
    delivery_type || '',
    consider_delivery_period ?? false,
    start_of_day,
    invocation,
    window.api_url
  );
}

/**
 * @customfunction GETLOADPROFILELIST
 * @param {string} [contract_portfolio_id]
 * @param {string} [managing_portfolio_id]
 * @param {string} [load_profile_name]
 * @param {string} [metering_point]
 * @param {string} [metering_type] [SLP;RLM;SM;LP;AGG]
 * @param {string} [load_profile_quality] [forecast;actual;estimate;forecast_draft]
 * @param {string} [delivery_type] [consumption; production]
 * @return {string[][]}
 */
export async function getLoadprofileList(
  contract_portfolio_id?: string,
  managing_portfolio_id?: string,
  load_profile_name?: string,
  metering_point?: string,
  metering_type?: string,
  load_profile_quality?: string,
  delivery_type?: string
) {
  return await loadProfileFunctions.getLoadProfileList(
    contract_portfolio_id,
    managing_portfolio_id,
    load_profile_name,
    metering_point,
    metering_type,
    load_profile_quality,
    delivery_type,
    window.api_url
  );
}

/**
 * @customfunction GETMETERINGPOINTLIST
 * @param contract_portfolio_id
 * @param managing_portfolio_id
 * @param status
 * @param load_profile_type
 * @param changed_after
 * @param updated_after
 * @return {string[][]}
 */
export async function getMeteringpointList(
  contract_portfolio_id?: string,
  managing_portfolio_id?: string,
  status?: string,
  load_profile_type?: string,
  changed_after?: string,
  updated_after?: string
) {
  return await meteringPointFunctions.getMeteringPointsList(
    contract_portfolio_id,
    managing_portfolio_id,
    status,
    load_profile_type,
    changed_after,
    updated_after,
    window.api_url
  );
}

/**
 * @customfunction LIMPR
 * @requiresAddress
 * @param {number} portfolio_id
 * @param {number} report_id
 * @param invocation
 * @return {string[][]}
 */
export async function getLimprReport(
  portfolio_id: number,
  report_id: number,
  invocation: CustomFunctions.Invocation
) {
  return await loadLimprReport(portfolio_id, report_id, invocation.address!);
}
//
// /**
//  * @customfunction GET_LOADPROFILE_LIST
//  * @param {string} [contract_portfolio_id]
//  * @param {string} [managing_portfolio_id]
//  * @param {string} [load_profile_name]
//  * @param {string} [metering_point]
//  * @param {string} [metering_type] [SLP;RLM;SM;LP;AGG]
//  * @param {string} [load_profile_quality] [forecast;actual;estimate;forecast_draft]
//  * @param {string} [quantity_type] [consumption; production]
//  * @return {string[][]}
//  */
// export async function get_loadprofile_list(
//   contract_portfolio_id?: string,
//   managing_portfolio_id?: string,
//   load_profile_name?: string,
//   metering_point?: string,
//   metering_type?: string,
//   load_profile_quality?: string,
//   quantity_type?: string
// ) {
//   return await loadProfileFunctions.getLoadProfileList(
//     contract_portfolio_id,
//     managing_portfolio_id,
//     load_profile_name,
//     metering_point,
//     metering_type,
//     load_profile_quality,
//     quantity_type,
//     'https://emsa.icservice.inercomp.local/v1'
//   );
// }
//
// /**
//  * @customfunction GET_AGGREGATED_LOADPROFILES
//  * @requiresAddress
//  * @param {string[][]} load_profile_ids
//  * @param {string} contract_portfolio_id
//  * @param {string[][]} managing_portfolio_ids
//  * @param {string} load_profile_type
//  * @param {string} metering_type
//  * @param {string} resolution
//  * @param {string} unit
//  * @param {number|string} start_date
//  * @param {number|string} end_date
//  * @param {boolean} gap_warning
//  * @param {boolean} auto_update
//  * @param {boolean} [exclude_gap_lps]
//  * @param {string} [delivery_type]
//  * @param {boolean} [consider_delivery_period]
//  * @param invocation
//  * @return {string[][]}
//  */
// export async function get_aggregated_loadprofiles(
//   load_profile_ids: string[][],
//   contract_portfolio_id: string,
//   managing_portfolio_ids: string[][],
//   load_profile_quality: string,
//   metering_point_type: string,
//   resolution: string,
//   unit: string,
//   start_date: number | string,
//   end_date: number | string,
//   gap_warning: boolean,
//   auto_update: boolean,
//   exclude_gap_lps: boolean = false,
//   delivery_type: string = '',
//   consider_delivery_period: boolean = false,
//   invocation: CustomFunctions.Invocation
// ) {
//   return await loadProfileFunctions.getAggregatedLoadProfiles(
//     load_profile_ids,
//     contract_portfolio_id,
//     managing_portfolio_ids,
//     load_profile_quality,
//     metering_point_type,
//     resolution,
//     unit,
//     start_date,
//     end_date,
//     gap_warning,
//     auto_update,
//     exclude_gap_lps,
//     delivery_type,
//     consider_delivery_period,
//     invocation,
//     'https://emsa.icservice.inercomp.local/v1'
//   );
// }
//
// /**
//  * @customfunction GET_METERINGPOINT_LIST
//  * @param contract_portfolio_id
//  * @param managing_portfolio_id
//  * @param status
//  * @param load_profile_type
//  * @param changed_after
//  * @param updated_after
//  * @return {string[][]}
//  */
// export async function get_meteringpoint_list(
//   contract_portfolio_id: string,
//   managing_portfolio_id: string,
//   status: string,
//   load_profile_type: string,
//   changed_after: string,
//   updated_after: string
// ) {
//   return await meteringPointFunctions.getMeteringPointsList(
//     contract_portfolio_id,
//     managing_portfolio_id,
//     status,
//     load_profile_type,
//     changed_after,
//     updated_after,
//     'https://emsa.icservice.inercomp.local/v1'
//   );
// }
//
// /**
//  * @customfunction GET_LOADPROFILE_BY_ID
//  * @requiresAddress
//  * @param {string[][]} load_profile_ids
//  * @param {string} unit (kW, MW, GW, kWh, MWh, GWh)
//  * @param {string} resolution (15 min, 1 hour, 1 day, 1 month)
//  * @param {number|string} start_date optional starting point of the time_series_data. (YYYY-MM-DDTHH:MM:SSZ)
//  * @param {number|string} end_date optional ending point of the time_series_data. (YYYY-MM-DDTHH:MM:SSZ)
//  * @param {boolean} enforce_length Extend the time_series from start_date to end_date if there is no data present in the db.
//  * @param {boolean} auto_update if set to true, Excel sheet will update custom functions with #NV values and once a day for other custom functions
//  * @param invocation
//  * @return {string[][]}
//  */
// export async function get_loadprofile_by_id(
//   load_profile_ids: string[][],
//   unit: string,
//   resolution: string,
//   start_date: number | string,
//   end_date: number | string,
//   enforce_length: boolean,
//   auto_update: boolean,
//   invocation: CustomFunctions.Invocation
// ) {
//   let id_arr = loadProfileFunctions.parseIds(load_profile_ids);
//   let start = getDateString(start_date);
//   let end = getDateString(end_date);
//   return loadProfileFunctions.getLoadProfilesByIds(
//     id_arr,
//     resolution,
//     unit,
//     start,
//     end,
//     enforce_length,
//     auto_update,
//     invocation.address!,
//     'https://emsa.icservice.inercomp.local/v1'
//   );
// }

CustomFunctions.associate("GETLIVEPRICESBYIDS", getLivePricesByIds);
CustomFunctions.associate("GETLIVEPRICESADVANCED", getLivePricesN);
CustomFunctions.associate("GETLIVEPRICES", getLivePrices);
CustomFunctions.associate("HPFC", getHPFC);
CustomFunctions.associate("GETHISTORICPRICES", getHistoricPrices);
CustomFunctions.associate("GETVERSIONEDPRICES", getHistoricPricesVersioned);
CustomFunctions.associate("GETFUNDAMENTALDATAOLD", getFundamentalDataOld);
CustomFunctions.associate("GETFUNDAMENTALDATADETAILED", getFundamentalDataDetailed);
CustomFunctions.associate("GETFUNDAMENTALDATA", getFundamentalDataDiagram);
CustomFunctions.associate("GETLOADPROFILEBYID", getLoadprofileById);
CustomFunctions.associate("GETAGGREGATEDLOADPROFILES", getAggregatedLoadprofiles);
CustomFunctions.associate("GETLOADPROFILELIST", getLoadprofileList);
CustomFunctions.associate("GETMETERINGPOINTLIST", getMeteringpointList);
CustomFunctions.associate("LIMPR", getLimprReport);